import { api } from "@app/api";
import { createSelector } from "@app/fx";
import { defaultEntity } from "@app/hal";
import { type WebState, schema } from "@app/schema";
import type {
  DeployLlmGatewayKey,
  DeployLlmGatewayKeyResponse,
} from "@app/types";

// A key without a limit of its own, including an API that doesn't send the
// field yet, has none rather than NaN.
const parseSpendLimit = (spendLimit: string | null | undefined) => {
  if (spendLimit === null || spendLimit === undefined) return null;

  const parsed = Number.parseFloat(spendLimit);
  return Number.isFinite(parsed) ? parsed : null;
};

type LimitedLlmGatewayKey = DeployLlmGatewayKey & { spendLimit: number };

export const deserializeLlmGatewayKey = (
  payload: DeployLlmGatewayKeyResponse,
): DeployLlmGatewayKey => ({
  id: `${payload.id}`,
  handle: payload.handle,
  organizationId: payload.organization_id,
  gatewayUrl: payload.gateway_url,
  tokenPrefix: payload.token_prefix,
  spendLimit: parseSpendLimit(payload.spend_limit),
  createdBy: payload.created_by,
  revokedAt: payload.revoked_at,
  revokedBy: payload.revoked_by,
  status: payload.status,
  createdAt: payload.created_at,
  updatedAt: payload.updated_at,
  // payload.token is intentionally dropped: the one-time secret reaches the UI
  // through createLlmGatewayKey's loader meta and never enters the store.
});

export const selectLlmGatewayKeyById = schema.llmGatewayKeys.selectById;
export const selectLlmGatewayKeysAsList =
  schema.llmGatewayKeys.selectTableAsList;

export const selectLlmGatewayKeysForTable = createSelector(
  selectLlmGatewayKeysAsList,
  (keys) =>
    [...keys].sort((a, b) =>
      b.id.localeCompare(a.id, undefined, { numeric: true }),
    ),
);

/**
 * The active keys the API would lower if the organization's spend limit dropped
 * to `spendLimit`: it caps every key's own limit at the organization's.
 */
export const selectLlmGatewayKeysAboveSpendLimit = createSelector(
  selectLlmGatewayKeysAsList,
  (_: WebState, props: { orgId: string; spendLimit: number | undefined }) =>
    props.orgId,
  (_: WebState, props: { orgId: string; spendLimit: number | undefined }) =>
    props.spendLimit,
  (keys, orgId, spendLimit): LimitedLlmGatewayKey[] =>
    keys.filter(
      (key): key is LimitedLlmGatewayKey =>
        spendLimit !== undefined &&
        key.organizationId === orgId &&
        key.status === "ACTIVE" &&
        key.spendLimit !== null &&
        key.spendLimit > spendLimit,
    ),
);

export const fetchLlmGatewayKeys = api.get<{ orgId: string }>(
  "/llm_gateway_keys?per_page=5000&organization_id=:orgId",
  function* (ctx, next) {
    if (!ctx.payload.orgId) {
      return;
    }

    yield* next();
    if (!ctx.json.ok) {
      return;
    }

    /* Clear stale keys so revoked or deleted ones don't linger between fetches.
     * This runs before halEntityParser writes the response, since that
     * middleware wraps the route and saves after this handler returns.
     */
    yield* schema.update(schema.llmGatewayKeys.reset());
  },
);

export const fetchLlmGatewayKeyById = api.get<{ id: string; orgId: string }>(
  "/llm_gateway_keys/:id?organization_id=:orgId",
);

export const createLlmGatewayKey = api.post<
  { handle: string; orgId: string; spendLimit?: number },
  DeployLlmGatewayKeyResponse
>("/llm_gateway_keys?organization_id=:orgId", function* (ctx, next) {
  const { handle, spendLimit } = ctx.payload;
  const body = {
    handle,
    ...(spendLimit === undefined ? {} : { spend_limit: spendLimit }),
  };
  ctx.request = ctx.req({ body: JSON.stringify(body) });

  yield* next();
  if (!ctx.json.ok) {
    return;
  }

  /* The token is shown once and never persisted, so it rides in loader meta
   * rather than the store. gatewayUrl comes along because the success modal
   * needs the endpoint matching this key's gateway.
   */
  ctx.loader = {
    meta: {
      token: ctx.json.value?.token || "",
      gatewayUrl: ctx.json.value?.gateway_url || "",
    },
  };
});

// A null spend limit removes the key's limit.
export const updateLlmGatewayKey = api.patch<{
  id: string;
  orgId: string;
  handle: string;
  spendLimit: number | null;
}>("/llm_gateway_keys/:id?organization_id=:orgId", function* (ctx, next) {
  const { handle, spendLimit } = ctx.payload;
  const body = { handle, spend_limit: spendLimit };
  ctx.request = ctx.req({ body: JSON.stringify(body) });

  yield* next();
  if (!ctx.json.ok) {
    return;
  }

  ctx.loader = { message: "Successfully updated key settings!" };
});

export const revokeLlmGatewayKey = api.delete<{ id: string; orgId: string }>(
  "/llm_gateway_keys/:id?organization_id=:orgId",
);

export const llmGatewayKeyEntities = {
  llm_gateway_key: defaultEntity({
    id: "llm_gateway_key",
    deserialize: deserializeLlmGatewayKey,
    save: schema.llmGatewayKeys.add,
  }),
};
